/**
 * The display helpers the EJS pages get from app.locals, ported so a figure reads identically on both
 * front ends. These are deliberately the same formulas, not "close enough": a home page that rounds to
 * $7.16M in one app and $7.2M in the other is the parity failure this migration is most likely to ship.
 */

export const money = (n: number | null | undefined): string =>
  n == null || Number.isNaN(n)
    ? '—'
    : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const nfmt = (n: number | null | undefined): string =>
  n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString('en-US');

/** Abbreviate money ≥ 1000 (the card figures): $7.16M, -$758.7K, $12.34. */
export const abbrM = (n: number | null | undefined): string | null => {
  if (n == null || Number.isNaN(n)) return null;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return sign + '$' + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return sign + '$' + (abs / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return sign + '$' + (abs / 1e3).toFixed(1) + 'K';
  return sign + '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Percentages: two decimals under 100, whole numbers above (a 1,234% position stays readable). */
export const pctF = (n: number): string => (Math.abs(n) < 100 ? n.toFixed(2) : String(Math.round(n)));

export const abbrPct = (p: number | null | undefined): string | null => {
  if (p == null || Number.isNaN(p)) return null;
  const sign = p < 0 ? '-' : '';
  const abs = Math.abs(p);
  if (abs > 100) return sign + Math.round(abs).toLocaleString('en-US') + '%';
  return sign + abs.toFixed(2) + '%';
};

/** A signed figure with its unit, the way the cards write day change and unrealized. */
export const signedPct = (n: number | null | undefined): string => (n == null || Number.isNaN(n) ? '' : ` (${pctF(n)}%)`);
